import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { CircularProgress } from "@mui/material";
import * as React from "react";

const ReactJson = React.lazy(() => import("@microlink/react-json-view"));

import {
  InvocationDebugRecord,
  ReconstructedMessage,
} from "@/shared/domain/admin";
import { useChatMessagePrompt } from "@/shared/domain/admin/hooks/useChatMessagePrompt";
import {
  type AgentThreadRecord,
  isAgentThreadToolResultContent,
  isAgentThreadToolUseContent,
} from "@/shared/domain/agentThread/AgentThreadRecord";
import { Button } from "@/shared/foundation/primitives";
import { useIsDarkMode } from "@/shared/hooks";

import styles from "./BedrockPromptView.module.css";
import { ReadOnlyAIAgentThreadProvider } from "./ReadOnlyAIAgentThreadProvider";
import { ReconstructedMessageRow } from "./ReconstructedMessageRow";
import {
  estimateTokens,
  formatTokenCount,
  formatTokensCompact,
} from "./tokenFormat";
import { summarizeTurnTokens } from "./turnTokenSummary";

interface ToolTokenSummary {
  name: string;
  totalTokens: number;
  inputSchemaTokens: number;
}

/**
 * Per-tool token costs (whole entry + just its inputSchema) for the summary
 * table above the raw JSON viewer. inputSchema is usually the bulk; surfacing
 * both lets you see at a glance whether a tool's weight is its prose or its
 * schema. Defensive about shape — ``tool_schemas`` is typed loosely and
 * a non-Bedrock entry just falls back to its overall size.
 */
function summarizeToolTokens(entry: unknown): ToolTokenSummary {
  const totalTokens = estimateTokens(entry);
  let name = "(unknown)";
  let inputSchema: unknown;
  if (entry !== null && typeof entry === "object") {
    const root = entry as Record<string, unknown>;
    const spec = (root.toolSpec ?? root) as Record<string, unknown>;
    if (typeof spec.name === "string") {
      name = spec.name;
    }
    inputSchema = spec.inputSchema;
  }
  return {
    name,
    totalTokens,
    inputSchemaTokens:
      inputSchema === undefined ? 0 : estimateTokens(inputSchema),
  };
}

/**
 * Header suffix showing the size of the prompt Bedrock processed, the
 * uncompressed-equivalent (when compression shaved off a meaningful amount),
 * and the prompt-cache read/write split.
 *
 * "Sent" is the *true* prompt size — uncached input plus cache reads plus
 * cache writes (see {@link summarizeTurnTokens}) — not the bare
 * ``input_tokens``, which is only the uncached remainder and is ~1 on a
 * well-cached turn. Falls back to the frontend's local
 * ``estimated_total_tokens`` when no invocation was recorded.
 */
function TurnTokenSummary({
  invocation,
  estimatedTotalTokens,
}: {
  invocation: InvocationDebugRecord | undefined;
  estimatedTotalTokens: number | undefined;
}): React.ReactNode {
  const summary = summarizeTurnTokens(invocation);
  if (summary === null) {
    if (estimatedTotalTokens === undefined) {
      return null;
    }
    return <> · ~{formatTokenCount(estimatedTotalTokens)} tokens</>;
  }

  const {
    sentTokens,
    cacheReadTokens,
    cacheWriteTokens,
    uncompressedTokens,
    savedPct,
  } = summary;
  return (
    <>
      {" · "}
      {formatTokenCount(sentTokens)} sent
      {uncompressedTokens !== null && (
        <span className={styles.metricMuted}>
          {" · "}~{formatTokenCount(uncompressedTokens)} uncompressed (−
          {savedPct}%)
        </span>
      )}
      {(cacheReadTokens > 0 || cacheWriteTokens > 0) && (
        <span className={styles.metricCache}>
          {" · "}
          {formatTokenCount(cacheReadTokens)} cache read ·{" "}
          {formatTokenCount(cacheWriteTokens)} written
        </span>
      )}
    </>
  );
}

interface BedrockPromptViewProps {
  /** The thread being debugged — only its id is needed to fetch the prompt. */
  agentThread: AgentThreadRecord;
  /** Sequence number of the assistant message whose prompt to reconstruct. */
  messageSequenceNum: number;
  /** Token usage row for this turn, if it was recorded. Drives the actual vs.
   *  original comparison shown in the header.
   */
  invocation: InvocationDebugRecord | undefined;
  /** Returns to the normal conversation view. */
  onExit: () => void;
}

/**
 * Replaces the conversation with what Bedrock saw when it generated one
 * message: a leading system-prompt + tools block, then the reconstructed
 * history — text through the shared chat components, tool requests/results
 * through the chat's own tool panel, compressed messages flagged with a
 * toggle to the uncompressed original.
 */
export function BedrockPromptView({
  agentThread,
  messageSequenceNum,
  invocation,
  onExit,
}: BedrockPromptViewProps) {
  const isDarkMode = useIsDarkMode();
  const { data, isLoading, error } = useChatMessagePrompt(
    agentThread.thread_id,
    messageSequenceNum,
    true,
  );

  // Index, by tool_use_id, the reconstructed message holding each tool's
  // result — so a tool_use can render request + response (and the result's
  // compression) in one panel, and the standalone result message is skipped.
  // Heavy-first so the tools driving prompt size sit at the top of the
  // table; an alphabetical sort hides the diagnostic signal.
  const toolSummaries = React.useMemo<ToolTokenSummary[]>(() => {
    if (!data) {
      return [];
    }
    return data.tool_schemas
      .map(summarizeToolTokens)
      .sort((a, b) => b.totalTokens - a.totalTokens);
  }, [data]);

  const { resultEntryByToolUseId, pairedToolUseIds } = React.useMemo(() => {
    const entryById = new Map<string, ReconstructedMessage>();
    const useIds = new Set<string>();
    for (const entry of data?.messages ?? []) {
      for (const block of entry.message.content) {
        if (isAgentThreadToolResultContent(block)) {
          entryById.set(block.tool_use_id, entry);
        }
        if (isAgentThreadToolUseContent(block)) {
          useIds.add(block.tool_use_id);
        }
      }
    }
    return { resultEntryByToolUseId: entryById, pairedToolUseIds: useIds };
  }, [data]);

  return (
    <div className={styles.viewer}>
      <div className={styles.backBar}>
        <Button
          size="small"
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={onExit}
        >
          Back to conversation
        </Button>
        <span className={styles.caption}>
          What Bedrock saw when generating message #{messageSequenceNum}
          <TurnTokenSummary
            invocation={invocation}
            estimatedTotalTokens={data?.estimated_total_tokens}
          />
        </span>
      </div>

      {isLoading && (
        <div className={styles.state}>
          <CircularProgress size="1rem" />
          Reconstructing prompt…
        </div>
      )}
      {error && (
        <div className={styles.state}>
          Failed to load prompt: {error.message}
        </div>
      )}
      {data && (
        <div className={styles.thread}>
          {data.truncated_message_count > 0 && (
            <div className={styles.truncationBanner}>
              {data.truncated_message_count}{" "}
              {data.truncated_message_count === 1
                ? "older message was"
                : "older messages were"}{" "}
              truncated to fit the context window — Bedrock never saw{" "}
              {data.truncated_message_count === 1 ? "it" : "them"}.
            </div>
          )}
          <details className={styles.section}>
            <summary className={styles.sectionSummary}>
              System prompt
              <span className={styles.sectionTokens}>
                ~{formatTokensCompact(estimateTokens(data.system_prompt ?? ""))}{" "}
                tok
              </span>
            </summary>
            <pre className={styles.pre}>{data.system_prompt ?? "(none)"}</pre>
          </details>
          <details className={styles.section}>
            <summary className={styles.sectionSummary}>
              Tool definitions ({data.tool_schemas.length})
              <span className={styles.sectionTokens}>
                ~{formatTokensCompact(estimateTokens(data.tool_schemas))} tok
              </span>
            </summary>
            <table className={styles.toolSummary}>
              <thead>
                <tr>
                  <th>Tool</th>
                  <th className={styles.toolSummaryNumeric}>Total</th>
                  <th className={styles.toolSummaryNumeric}>inputSchema</th>
                </tr>
              </thead>
              <tbody>
                {toolSummaries.map((summary) => (
                  <tr key={summary.name}>
                    <td>{summary.name}</td>
                    <td className={styles.toolSummaryNumeric}>
                      ~{formatTokensCompact(summary.totalTokens)} tok
                    </td>
                    <td className={styles.toolSummaryNumeric}>
                      ~{formatTokensCompact(summary.inputSchemaTokens)} tok
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className={styles.jsonBlock}>
              <React.Suspense
                fallback={
                  <pre>{JSON.stringify(data.tool_schemas, null, 2)}</pre>
                }
              >
                <ReactJson
                  src={data.tool_schemas}
                  theme={isDarkMode ? "monokai" : "rjv-default"}
                  name={false}
                  displayDataTypes={false}
                  displayObjectSize={false}
                  enableClipboard={false}
                  collapsed={2}
                  style={{
                    backgroundColor: "transparent",
                    fontFamily: "inherit",
                    fontSize: "inherit",
                  }}
                />
              </React.Suspense>
            </div>
          </details>
          <ReadOnlyAIAgentThreadProvider>
            {data.messages.map((entry, index) => (
              // The reconstructed list is static and never reordered.
              <ReconstructedMessageRow
                key={index}
                entry={entry}
                resultEntryByToolUseId={resultEntryByToolUseId}
                pairedToolUseIds={pairedToolUseIds}
              />
            ))}
          </ReadOnlyAIAgentThreadProvider>
        </div>
      )}
    </div>
  );
}
